import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AlertCircle, ArrowLeft, Flag, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { seoHead } from "@/lib/seo";

const SearchSchema = z.object({
  generation_id: z.string().uuid().optional(),
});

export const Route = createFileRoute("/report-form")({
  head: () =>
    seoHead({
      title: "通報フォーム | KAGEM",
      description: "KAGEMで生成されたコンテンツや利用規約違反の疑いがあるコンテンツを通報するフォームです。",
      path: "/report-form",
      noindex: true,
    }),
  validateSearch: (search) => SearchSchema.parse(search),
  component: ReportFormPage,
});

const REASON_OPTIONS: { value: string; label: string }[] = [
  { value: "adult", label: "成人向けコンテンツ" },
  { value: "violence", label: "暴力的・有害な内容" },
  { value: "impersonation", label: "なりすまし・有名人の使用" },
  { value: "copyright", label: "著作権侵害" },
  { value: "minor", label: "未成年関連" },
  { value: "hate", label: "ヘイトスピーチ・差別" },
  { value: "other", label: "その他" },
];

const DETAIL_MAX = 500;

function ReportFormPage() {
  const navigate = useNavigate();
  const { generation_id: generationId } = Route.useSearch();

  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [authChecked, setAuthChecked] = useState(false);

  const [genCreatedAt, setGenCreatedAt] = useState<string | null>(null);
  const [genThumb, setGenThumb] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(true);

  const [reasons, setReasons] = useState<string[]>([]);
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!active) return;
      if (data.user) {
        setUserId(data.user.id);
        setUserEmail(data.user.email ?? "");
      }
      setAuthChecked(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  // 未ログインは案内画面を表示（reCAPTCHA未実装のため自動リダイレクトはしない）

  // 対象動画の最低限の情報のみ取得（RLSで他人の動画は取れないが、admin か本人なら取れる）
  useEffect(() => {
    if (!generationId || !userId) return;
    let active = true;
    (async () => {
      setGenLoading(true);
      const { data } = await supabase
        .from("generations")
        .select("created_at, output_video_url")
        .eq("id", generationId)
        .maybeSingle();
      if (!active) return;
      // RLS で取れなくても通報自体は可能（送信時にサーバが存在確認）
      if (data) {
        setGenCreatedAt(data.created_at);
        setGenThumb(data.output_video_url);
      }
      setGenLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [generationId, userId]);

  const canSubmit = useMemo(
    () => !!generationId && reasons.length > 0 && !submitting && !!userId,
    [generationId, reasons.length, submitting, userId],
  );

  const toggleReason = (value: string, checked: boolean) => {
    setReasons((prev) =>
      checked ? Array.from(new Set([...prev, value])) : prev.filter((v) => v !== value),
    );
  };

  const onSubmit = async () => {
    if (!canSubmit || !generationId) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-report", {
        body: {
          generation_id: generationId,
          reasons,
          detail: detail.trim() || undefined,
          reporter_email: userEmail || undefined,
        },
      });
      if (error) {
        const ctx = (error as unknown as { context?: Response }).context;
        let msg = "通報の送信に失敗しました";
        if (ctx) {
          try {
            const body = await ctx.clone().json();
            if (body?.error === "DUPLICATE_REPORT") {
              msg = "同じ動画への通報を最近受け付けています。少し時間をおいてから再度お試しください。";
            } else if (body?.error === "GENERATION_NOT_FOUND") {
              msg = "対象の動画が見つかりませんでした";
            } else if (body?.error === "REASON_REQUIRED") {
              msg = "通報理由を1つ以上選択してください";
            } else if (body?.error === "LOGIN_REQUIRED") {
              msg = "ログインが必要です";
            }
          } catch {
            /* ignore */
          }
        }
        toast.error(msg);
        return;
      }
      const okMsg = (data as { message?: string } | null)?.message ??
        "通報を受け付けました。確認後に対応いたします（24〜48時間以内）";
      track("report_submit", { reasons });
      toast.success(okMsg);
      navigate({ to: "/dashboard" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  if (!generationId) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">対象が指定されていません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              通報対象の動画IDが必要です。動画ページの「通報」リンクからアクセスしてください。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  // 未ログイン時の案内画面（reCAPTCHA未実装のため、現状はログイン必須）
  if (!userId) {
    const loginRedirect = `/report-form?generation_id=${generationId ?? ""}`;
    return (
      <main className="min-h-screen bg-background px-4 py-12">
        <div className="mx-auto max-w-md">
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/report">
              <ArrowLeft className="mr-1 h-4 w-4" />
              戻る
            </Link>
          </Button>
          <Card className="p-6 sm:p-8">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Flag className="h-6 w-6 text-primary" />
            </div>
            <h1 className="mt-4 text-center text-xl font-bold">
              通報にはログインが必要です
            </h1>
            <p className="mt-3 text-center text-sm leading-relaxed text-muted-foreground">
              通報の信頼性を担保するため、現在は会員登録／ログインが必要です。
              アカウントをお持ちでない方は、お問い合わせフォームからご連絡ください。
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild>
                <Link to="/login" search={{ redirect: loginRedirect } as never}>
                  ログインして通報する
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/contact">
                  <Mail className="mr-2 h-4 w-4" />
                  お問い合わせフォームへ
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/dashboard">
            <ArrowLeft className="mr-1 h-4 w-4" />
            戻る
          </Link>
        </Button>

        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <Flag className="h-5 w-5 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">動画を通報する</h1>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            内容を確認のうえ、24〜48時間以内に対応します。虚偽の通報を繰り返した場合、アカウント停止の対象となることがあります。
          </p>

          {/* 対象動画の概要 */}
          <div className="mt-6 rounded-lg border bg-muted/40 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              通報対象
            </p>
            {genLoading ? (
              <div className="mt-2 flex items-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                読み込み中...
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-3">
                {genThumb ? (
                  <video
                    src={genThumb}
                    className="h-16 w-16 rounded object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div className="h-16 w-16 rounded bg-muted" />
                )}
                <div className="text-sm">
                  <p className="font-mono text-xs text-muted-foreground">
                    ID: {generationId.slice(0, 8)}…
                  </p>
                  {genCreatedAt && (
                    <p className="mt-1 text-muted-foreground">
                      生成日時: {new Date(genCreatedAt).toLocaleString("ja-JP")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 理由 */}
          <div className="mt-6">
            <Label className="text-sm font-semibold">
              通報理由（1つ以上選択） <span className="text-destructive">*</span>
            </Label>
            <div className="mt-3 space-y-2">
              {REASON_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-3 rounded-md border bg-background p-3 hover:bg-muted/40"
                >
                  <Checkbox
                    checked={reasons.includes(opt.value)}
                    onCheckedChange={(c) => toggleReason(opt.value, c === true)}
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 詳細 */}
          <div className="mt-6">
            <Label htmlFor="detail" className="text-sm font-semibold">
              詳細説明（任意）
            </Label>
            <Textarea
              id="detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, DETAIL_MAX))}
              placeholder="具体的な状況や背景があれば記入してください"
              className="mt-2 min-h-[100px]"
              maxLength={DETAIL_MAX}
            />
            <p className="mt-1 text-right text-xs text-muted-foreground">
              {detail.length} / {DETAIL_MAX}
            </p>
          </div>

          {/* 連絡先 */}
          <div className="mt-6">
            <Label htmlFor="email" className="text-sm font-semibold">
              連絡先メールアドレス
            </Label>
            <Input
              id="email"
              type="email"
              value={userEmail}
              readOnly
              disabled
              className="mt-2 bg-muted"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              ログイン中のメールアドレスを使用します
            </p>
          </div>

          {/* 送信 */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="ghost" asChild>
              <Link to="/dashboard">キャンセル</Link>
            </Button>
            <Button onClick={onSubmit} disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  送信中...
                </>
              ) : (
                "通報を送信"
              )}
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
